import React, { useState, useEffect, useRef } from 'react';
import { generateAiSummary } from '../api';
import { formatMarkdown } from '../utils/markdownFormatter';
import { format } from 'date-fns';

interface SlidingAiPanelProps {
  insight: string;
  contextType: 'overview' | 'iteration' | 'team' | 'developer' | 'project' | 'project-developer' | 'developer-iteration' | 'all-iterations' | 'support-overview' | 'support-team' | 'support-team-member' | 'support-customer' | 'support-all-customers';
  entityName?: string;
  entityId?: string;
  projectId?: string;
  summaryId?: string;
  summaryCreatedOn?: string;
  canGenerateNewSummary?: boolean;
  generateIfMissing?: boolean;
  initialOpen?: boolean;
  shouldCloseOnOutisdeClick?: boolean;
  buttonTitle?: string;
  onStreamedContentChange?: (summary: string, createdOn: string) => void;
  generateInitialAi?: () => Promise<{summary: string, createdOn: string}>;
}


// Global persistent cache for content across component instances
const globalContentCache: Record<string, {summary: string, createdOn: string}> = {};
const globalActiveStreams: Record<string, ReadableStreamDefaultReader> = {};

const SlidingAiPanel: React.FC<SlidingAiPanelProps> = ({
  insight = '',
  contextType,
  entityName = '',
  entityId = '',
  projectId,
  summaryId,
  summaryCreatedOn = '',
  canGenerateNewSummary = true,
  generateIfMissing = true,
  initialOpen = false,
  shouldCloseOnOutisdeClick = true,
  buttonTitle = 'Get Insights',
  onStreamedContentChange,
  generateInitialAi
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(initialOpen);
  const [contentCache, setContentCache] = useState<Record<string, {summary: string, createdOn: string}>>(globalContentCache);
  const [activeStreams, setActiveStreams] = useState<Record<string, ReadableStreamDefaultReader>>(globalActiveStreams);
  const [isInitialAiLoading, setIsInitialAiLoading] = useState<boolean>(false);
  const [createdOn, setCreatedOn] = useState<string>(summaryCreatedOn);
  const [canGenerate, setCanGenerate] = useState<boolean>(canGenerateNewSummary);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Generate cache key
  const getCacheKey = (name: string, id: string) => `${name}-${id}`;
  const currentCacheKey = getCacheKey(entityName, entityId);

  // Get current content for rendering
  //const currentContent = contentCache[currentCacheKey] || '';

  // Check if current entity is generating
  const isCurrentEntityGenerating = activeStreams[currentCacheKey] !== undefined;

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize(); // Set initial size
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle click outside to close panel
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shouldCloseOnOutisdeClick && panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isMobile]);

    useEffect(() => {
      if (summaryCreatedOn) {
        // Check if it's already a localized date string by trying to parse it and compare
        const parsedDate = new Date(summaryCreatedOn);
        
        // If parsedDate is valid and the string does NOT match the toLocaleDateString of that date
        if (!isNaN(parsedDate.getTime()) && summaryCreatedOn !== parsedDate.toLocaleDateString()) {
          const d = format(parsedDate, 'do MMMM yyyy');
          setCreatedOn(d);
        } else {
          const d = format(summaryCreatedOn, 'do MMMM yyyy');
          setCreatedOn(d);
        }
      }
    }, [summaryCreatedOn]);
  
    useEffect(() => {
      setCanGenerate(canGenerateNewSummary);
    }, [canGenerateNewSummary]);

  // Update cache when entityId or entityName changes
  useEffect(() => {
    // When entityId or entityName changes, we need to update the cache
    // with the current insight for the new entity
    if (entityId && insight) {
      const insightData = {
        summary: insight,
        createdOn: createdOn
      };
      const newCacheKey = getCacheKey(entityName, entityId);
      const newCache = { ...contentCache, [newCacheKey]: insightData };
      setContentCache(newCache);
      // Update global cache
      globalContentCache[newCacheKey] = insightData;
    }
  }, [entityId, entityName, insight]);

  // Initialize cache with insight prop when it changes
  useEffect(() => {
    if (insight && currentCacheKey) {
      const newCache = { ...contentCache, [currentCacheKey]: { summary: insight, createdOn: createdOn } };
      setContentCache(newCache);
      // Update global cache
      globalContentCache[currentCacheKey] = { summary: insight, createdOn: createdOn };
    }
  }, [insight, currentCacheKey]);

  // Handle initial AI loading via delegate if provided
  useEffect(() => {
    if (generateInitialAi && !isInitialAiLoading) {
      //start loading spinner
      setIsInitialAiLoading(true);
      const loadInitialAi = async () => {
        try {
          const result = await generateInitialAi() || null;
          if(result.createdOn){
            const d = format(result.createdOn, 'do MMMM yyyy');
            setCreatedOn(d);
          }
          else{
            setCreatedOn('');
          }
          
          if (currentCacheKey) {
            const newCache = { ...contentCache, [currentCacheKey]: { summary: result.summary, createdOn: result.createdOn } };
            setContentCache(newCache);
            // Update global cache
            globalContentCache[currentCacheKey] = { summary: result.summary, createdOn: result.createdOn };
            // Notify parent if callback provided
            if (onStreamedContentChange) {
              onStreamedContentChange(result.summary, result.createdOn);
            }
            setIsInitialAiLoading(false);
          }
        } catch (error) {
          console.error('Error loading initial AI:', error);
        } finally {
          setIsInitialAiLoading(false);
        }
      };

      loadInitialAi();
    }
  }, [entityId]);
  //}, [generateInitialAi]);

  // Cleanup streams on component unmount
  useEffect(() => {
    return () => {
      // Cancel all active streams when component unmounts
      Object.values(activeStreams).forEach(stream => {
        if (stream && typeof stream.cancel === 'function') {
          stream.cancel().catch(console.error);
        }
      });
    };
  }, []);

  // Add useEffect to load Material Icons font
  useEffect(() => {
    // Add Material Icons font if it doesn't exist
    if (!document.querySelector('link[href*="material-icons"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      document.head.appendChild(link);
    }

    // Cleanup function not needed as we want to keep the font loaded
  }, []);

  const handleGenerateSummary = async (skipCache = false) => {
    if (!entityId || !currentCacheKey) return;

    // Check if stream is already active for this entity
    if (activeStreams[currentCacheKey] && !skipCache) {
      return;
    }

    // If we have cached content and not forcing refresh, use it
    if (contentCache[currentCacheKey] && !skipCache) {
      return;
    }

    try {
      //needs to show a loading spinner when the button is clicked and before the response starts streaming
      setIsInitialAiLoading(true)
      // Cancel existing stream for this entity if forcing refresh
      if (activeStreams[currentCacheKey] && skipCache) {
        await activeStreams[currentCacheKey].cancel();
        const newActiveStreams = { ...activeStreams };
        delete newActiveStreams[currentCacheKey];
        setActiveStreams(newActiveStreams);
        delete globalActiveStreams[currentCacheKey];
      }

      // Clear existing content if forcing refresh
      if (skipCache) {
        const newCache = { ...contentCache };
        delete newCache[currentCacheKey];
        setContentCache(newCache);
        delete globalContentCache[currentCacheKey];
      }

      const forceRegenerate = true;
      const stream = await generateAiSummary(contextType, entityId, projectId, summaryId, generateIfMissing,forceRegenerate);
      //remove loading dialog
      setIsInitialAiLoading(false);
      // Track this stream
      const newActiveStreams = { ...activeStreams, [currentCacheKey]: stream.stream };
      setActiveStreams(newActiveStreams);
      globalActiveStreams[currentCacheKey] = stream.stream;

      // Stream content directly to cache
      let accumulatedContent = '';
      for await (const chunk of stream) {
        accumulatedContent += chunk;
        const date = new Date().toISOString();
        const newCache = { ...contentCache, [currentCacheKey]: { summary: accumulatedContent, createdOn: date } };
        setContentCache(newCache);
        globalContentCache[currentCacheKey] = { summary: accumulatedContent, createdOn: date };
      }
      const d = format(new Date(), 'do MMMM yyyy');
      setCreatedOn(d);
      setCanGenerate(false);

    } catch (err) {
      console.error('Error generating summary:', err);
      setIsInitialAiLoading(false);
    } finally {
      // Clean up completed stream
      const newActiveStreams = { ...activeStreams };
      delete newActiveStreams[currentCacheKey];
      setActiveStreams(newActiveStreams);
      delete globalActiveStreams[currentCacheKey];
      setIsInitialAiLoading(false);
    }
  };



  return (
    <>
      {/* Mobile Implementation */}
      {isMobile ? (
        <>
          {/* Mobile Drawer Handle */}
          <div 
            onClick={() => setIsOpen(!isOpen)}
            style={{
              position: 'fixed',
              bottom: isOpen ? '76vh' : '0',
              left: '0',
              right: '0',
              height: '60px',
              background: 'linear-gradient(115deg, rgb(34, 34, 69) 25%, rgb(77, 182, 159) 162%)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              transition: 'bottom 0.3s ease-in-out',
              borderTopLeftRadius: isOpen ? '0' : '20px',
              borderTopRightRadius: isOpen ? '0' : '20px',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.15)'
            }}
          >
            <div style={{
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1rem',
              fontWeight: '500'
            }}>
              <span>{buttonTitle}</span>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                style={{
                  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <path d="M18 15l-6-6-6 6"/>
              </svg>
            </div>
          </div>

          {/* Mobile Drawer Content */}
          <div 
            ref={panelRef}
            style={{
              position: 'fixed',
              bottom: isOpen ? '0' : '-76vh',
              left: '0',
              right: '0',
              height: '76vh',
              background: '#f5f7ff',
              zIndex: 999,
              transition: 'bottom 0.3s ease-in-out',
              //borderTopLeftRadius: '20px',
              //borderTopRightRadius: '20px',
              //boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{
              height: '100%',
              padding: '1rem',
              paddingTop: '2rem',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column'
            }}>
              {renderPanelContent()}
            </div>
          </div>

          {/* Mobile Overlay */}
          {isOpen && (
            <div 
              onClick={() => setIsOpen(false)}
              style={{
                position: 'fixed',
                top: '0',
                left: '0',
                right: '0',
                bottom: '76vh',
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 998,
                transition: 'opacity 0.3s ease-in-out'
              }}
            />
          )}
        </>
      ) : (
        <>
          {/* Desktop Implementation */}
          {/* Overlay background when panel is open */}
          {isOpen && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100vh',
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 998,
                backdropFilter: 'blur(2px)'
              }}
              onClick={() => setIsOpen(false)}
            />
          )}

          {/* Desktop Sliding Panel */}
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: '20px',
              bottom: '20px',
              right: isOpen ? '0' : '-50%',
              width: '50%',
              height: 'auto',
              maxHeight: 'calc(100vh - 40px)',
              background: '#f5f7ff',
              color: '#333',
              transition: 'right 0.3s ease-in-out',
              boxShadow: '-5px 0 15px rgba(0, 0, 0, 0.2)',
              zIndex: 999,
              display: 'flex',
              flexDirection: 'column',
              padding: '2rem',
              overflowY: 'auto',
              borderRadius: '8px 0 0 8px',
              opacity: isOpen ? 1 : 0,
              visibility: 'visible',
              transitionProperty: 'right, opacity',
              transitionDuration: '0.3s',
              transitionTimingFunction: 'ease-in-out'
            }}
          >
            {renderPanelContent()}
          </div>

          {/* Desktop Trigger Button (visible when panel is closed) */}
          {!isOpen && (
            <div
              aria-label="Open Exec Insights"
              onClick={() => setIsOpen(true)}
              style={{
                position: 'fixed',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                background: '#1a237e',
                color: 'white',
                border: 'none',
                borderRadius: '4px 0 0 4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                padding: '12px 8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#3949ab';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#1a237e';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
              }}
            >
              <div style={{
                writingMode: 'vertical-rl',
                textOrientation: 'mixed',
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '1px',
                padding: '8px 0'
              }}>
                {buttonTitle}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );

  // Helper function to render panel content
  function renderPanelContent() {
    return (
      <>
        {/* Close Button - Desktop only */}
        {!isMobile && (
          <button
            aria-label="Close Exec Insights"
            onClick={() => setIsOpen(false)}
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              background: 'rgba(57, 73, 171, 0.1)',
              color: '#3949ab',
              border: 'none',
              borderRadius: 20,
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 1000
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}

        {/* Title and Regenerate Button */}
        {(insight || contentCache[currentCacheKey]) && (
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: isMobile ? 'center' : 'space-between',
              alignItems: isMobile ? 'center' : 'center',
              textAlign: isMobile ? 'center' : 'left',
              gap: isMobile ? '0.5rem' : '0',
              marginBottom: isMobile ? '0.5rem' : '1.5rem',
              paddingLeft: isMobile ? '0' : '48px',
              paddingBottom: isMobile ? '1rem' : '0',
              borderBottom: isMobile ? '3px solid #1a237e' : 'none' 
            }}
          >
            <h2 style={{ margin: 0, color: '#1a237e' }}>
              Exec Insights {entityName && `- ${entityName}`}
            </h2>

            {/* Desktop right-side actions */}
            {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {createdOn && (
                  <span style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic' }}>
                    Generated: {createdOn}
                  </span>
                )}
                {canGenerate && (
                  <button
                    onClick={() => handleGenerateSummary(true)}
                    disabled={isCurrentEntityGenerating}
                    title="Refresh Analysis"
                    style={{
                      background: isCurrentEntityGenerating ? 'rgba(57, 73, 171, 0.2)' : 'rgba(57, 73, 171, 0.15)',
                      color: '#3949ab',
                      border: '1px solid rgba(57, 73, 171, 0.3)',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: isCurrentEntityGenerating ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      if (!isCurrentEntityGenerating) {
                        e.currentTarget.style.background = 'rgba(57, 73, 171, 0.25)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrentEntityGenerating) {
                        e.currentTarget.style.background = 'rgba(57, 73, 171, 0.15)';
                      }
                    }}
                  >
                    {isCurrentEntityGenerating ? (
                      <>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid rgba(57, 73, 171, 0.3)',
                            borderTop: '2px solid #3949ab',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}
                        />
                        Generating...
                      </>
                    ) : (
                      'Regenerate'
                    )}
                  </button>
                )}
              </div>
            )}

            {/* Mobile: actions underneath title, centered */}
            {isMobile && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                {canGenerate ? (
                  <button
                    onClick={() => handleGenerateSummary(true)}
                    disabled={isCurrentEntityGenerating}
                    title="Refresh Analysis"
                    style={{
                      background: isCurrentEntityGenerating ? 'rgba(57, 73, 171, 0.2)' : 'rgba(57, 73, 171, 0.15)',
                      color: '#3949ab',
                      border: '1px solid rgba(57, 73, 171, 0.3)',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      cursor: isCurrentEntityGenerating ? 'not-allowed' : 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      backdropFilter: 'blur(10px)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isCurrentEntityGenerating ? (
                      <>
                        <div
                          style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid rgba(57, 73, 171, 0.3)',
                            borderTop: '2px solid #3949ab',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}
                        />
                        Generating...
                      </>
                    ) : (
                      'Regenerate'
                    )}
                  </button>
                ) : (
                  createdOn && (
                    <span style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic' }}>
                      Generated: {createdOn}
                    </span>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {/* Generate Button or Insight Content */}
        {!insight && (!contentCache[currentCacheKey] || contentCache[currentCacheKey].summary === '') ? (
          <div style={{ textAlign: 'center', padding: '2rem', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <button
              onClick={() => handleGenerateSummary(true)}
              disabled={isCurrentEntityGenerating}
              style={{
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                padding: '16px 24px',
                cursor: isCurrentEntityGenerating ? 'not-allowed' : 'pointer',
                opacity: isCurrentEntityGenerating ? 0.7 : 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transform: 'translateY(0)'
              }}
              onMouseEnter={(e) => {
                if (!isCurrentEntityGenerating) {
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {isInitialAiLoading ? (
                <>
                  <div style={{ width: '48px', height: '48px', position: 'relative' }}>
                    <div style={{
                      border: '4px solid #f3f3f3',
                      borderTop: '4px solid #3949ab',
                      borderRadius: '50%',
                      width: '40px',
                      height: '40px',
                      animation: 'spin 1s linear infinite',
                      position: 'absolute',
                      top: '4px',
                      left: '4px'
                    }}></div>
                  </div>
                  <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#666' }}>
                    Loading insights...
                  </div>
                  <style>{`
                    @keyframes spin {
                      0% { transform: rotate(0deg); }
                      100% { transform: rotate(360deg); }
                    }
                  `}</style>
                </>
              ) : (
                <>
                  <span className="material-icons" style={{ fontSize: '48px', color: '#3949ab' }}>
                    bar_chart
                  </span>
                  <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#666' }}>
                    Generate analysis for {entityName}
                  </div>
                </>
              )}
            </button>
          </div>
        ) : (
          <div
            className="markdown-content"
            style={{
              marginBottom: '2rem',
              fontSize: isMobile ? 14 : 16,
              lineHeight: 1.6,
              flex: 1,
              overflowY: 'auto',
              color: '#333',
              padding: isMobile ? '8px' : '16px'
            }}
            dangerouslySetInnerHTML={{ __html: formatMarkdown(contentCache[currentCacheKey]?.summary || insight) }}
          />
        )}

        {/* Add custom styling for markdown content */}
        <style>{`
          .markdown-content h2 {
            color: #143e87;
            font-size: 1.3rem;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
          }
          .markdown-content h3 {
            color: #143e87;
            font-size: 1.1rem;
            margin-top: 1.2rem;
            margin-bottom: 0.5rem;
          }
          .markdown-content p {
            margin-bottom: 1rem;
          }
          .markdown-content ul, .markdown-content ol {
            margin-left: 1.5rem;
            margin-bottom: 1rem;
          }
          .markdown-content li {
            margin-bottom: 0.5rem;
          }
          .markdown-content strong {
            color: #143e87;
            font-weight: 600;
          }
        `}</style>
      </>
    );
  }
};

export default SlidingAiPanel;
